import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { doc, onSnapshot, collection, query, where, getDocs, getDoc } from "firebase/firestore";
import { db as firestoreDb } from "@/src/lib/firebase";
import { useAuth } from "@/src/lib/AuthContext";
import { Product, Shop, Review } from "@/src/types";
import { Loader2, ArrowLeft, MessageCircle, Star, Store, ShieldCheck, Truck, Share2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { getWhatsAppLink, shortenUrl } from "@/src/lib/utils";
import { ShareModal } from "../components/ShareModal";

export function ProductDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [shop, setShop] = useState<Shop | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    const productRef = doc(firestoreDb, "products", id);
    const unsubscribe = onSnapshot(productRef, async (productSnap) => {
      if (productSnap.exists()) {
        const productData = { ...productSnap.data(), id: productSnap.id } as Product;
        setProduct(productData);
        
        // Fetch shop data
        const shopDoc = await getDoc(doc(firestoreDb, "shops", productData.shopId));
        if (shopDoc.exists()) {
          setShop({ ...shopDoc.data(), id: shopDoc.id } as Shop);
        }

        // Fetch reviews for this product
        const reviewsQuery = query(
          collection(firestoreDb, "reviews"), 
          where("targetId", "==", id)
        );
        const reviewsSnap = await getDocs(reviewsQuery);
        const allReviews = reviewsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Review));
        setReviews(allReviews.filter(r => r.isVisible));
        setLoading(false);
      } else {
        setProduct(null);
        setLoading(false);
      }
    }, (error) => {
      console.error("Error listening to product:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [id]);

  const handleWhatsAppPurchase = async () => {
    if (!shop?.whatsapp) {
      toast.error("Esta tienda no tiene WhatsApp configurado");
      return;
    }
    
    setLoading(true);
    try {
      const shortUrl = await shortenUrl(
        window.location.href, 
        shop.name, 
        product?.name, 
        product?.images?.[0] || shop.logoURL,
        product?.description
      );
      const priceText = product?.priceToAgree ? "Precio a convenir" : `$${product?.price.toLocaleString()}`;
      const message = `Hola ${shop.name}! Me interesa este producto: ${product?.name}\nPrecio: ${priceText}\nLink: ${shortUrl}\n\n*Pedido realizado por:* ${user?.displayName || "Usuario"}\n*Email:* ${user?.email}`;
      window.open(getWhatsAppLink(shop.whatsapp, message), "_blank");
    } catch (error) {
      console.error("Error shortening URL:", error);
      const priceText = product?.priceToAgree ? "Precio a convenir" : `$${product?.price.toLocaleString()}`;
      const message = `Hola ${shop.name}! Me interesa este producto: ${product?.name}\nPrecio: ${priceText}\nLink: ${window.location.href}\n\n*Pedido realizado por:* ${user?.displayName || "Usuario"}\n*Email:* ${user?.email}`;
      window.open(getWhatsAppLink(shop.whatsapp, message), "_blank");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container py-24 text-center">
        <h2 className="text-2xl font-bold mb-4">Producto no encontrado</h2>
        <Button onClick={() => navigate(-1)}>Volver atrás</Button>
      </div>
    );
  }

  return (
    <div className="container py-12 px-4 md:px-8">
      <Button 
        variant="ghost" 
        className="mb-8 gap-2" 
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="h-4 w-4" /> Volver
      </Button>

      <div className="grid gap-12 md:grid-cols-2">
        {/* Image Gallery */}
        <div className="flex flex-col gap-4">
          <div className="aspect-square rounded-3xl overflow-hidden bg-muted border">
            <img 
              src={product.images[0] || `https://picsum.photos/seed/${product.id}/800/800`} 
              alt={product.name}
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          </div>
          <div className="grid grid-cols-4 gap-4">
            {product.images.slice(1).map((img, i) => (
              <div key={i} className="aspect-square rounded-xl overflow-hidden bg-muted border">
                <img src={img} alt={`${product.name} ${i}`} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              </div>
            ))}
          </div>
        </div>

        {/* Product Info */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{product.category}</Badge>
              <Badge variant="outline">{product.type === "product" ? "Producto" : "Servicio"}</Badge>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{product.name}</h1>
            <div className="flex items-center gap-4 mt-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2 rounded-full"
                onClick={handleShare}
              >
                <Share2 className="h-4 w-4" /> Compartir
              </Button>
              <div className="flex items-center text-sm font-medium">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 mr-1" />
                {(product.rating || 0).toFixed(1)}
                <span className="text-muted-foreground ml-1">({product.reviewCount || 0} reseñas)</span>
              </div>
              {product.stock !== undefined && (
                <Badge variant={product.stock > 0 ? "outline" : "destructive"}>
                  {product.stock > 0 ? `${product.stock} disponibles` : "Agotado"}
                </Badge>
              )}
            </div>
          </div>

          <div className="text-4xl font-bold text-primary">
            {product.priceToAgree ? "Precio a convenir" : `$${product.price.toLocaleString()}`}
          </div>

          <p className="text-muted-foreground leading-relaxed">
            {product.description}
          </p>

          <div className="flex flex-col gap-3">
            {user?.uid === shop?.ownerUid && (
              <Link to="/dashboard">
                <Button variant="outline" className="w-full gap-2 mb-2">
                  Administrar Producto
                </Button>
              </Link>
            )}
            {user ? (
              <Button 
                size="lg" 
                className="w-full gap-2 h-14 text-lg bg-[#25D366] hover:bg-[#128C7E] text-white border-none" 
                onClick={handleWhatsAppPurchase} 
                disabled={product.stock === 0}
              >
                <MessageCircle className="h-5 w-5" /> Comprar por WhatsApp
              </Button>
            ) : (
              <Button 
                size="lg" 
                className="w-full gap-2 h-14 text-lg"
                onClick={() => navigate('/auth')}
              >
                <User className="h-5 w-5" /> Inicia sesión para comprar
              </Button>
            )}
          </div>

          <Separator className="my-4" />

          {/* Shop Info */}
          {shop && (
            <Link to={`/shop/${shop.id}`} className="flex items-center gap-4 p-4 rounded-2xl border hover:bg-muted/50 transition-colors">
              <div className="h-12 w-12 rounded-full overflow-hidden border bg-white">
                <img src={shop.logoURL || `https://picsum.photos/seed/${shop.name}/100/100`} alt={shop.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">Vendido por</div>
                <div className="font-bold flex items-center gap-1">
                  {shop.name} <Store className="h-3 w-3" />
                </div>
              </div>
              <Button variant="ghost" size="sm">Ver tienda</Button>
            </Link>
          )}

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" /> Compra Protegida
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Truck className="h-4 w-4 text-primary" /> Envío a todo el país
            </div>
          </div>
        </div>
      </div>

      <Separator className="my-12" />

      {product && (
        <ShareModal 
          isOpen={isShareModalOpen}
          onOpenChange={setIsShareModalOpen}
          originalUrl={window.location.href}
          defaultTitle={`${product.name} | ${shop?.name || "EmpreMarket"}`}
          defaultDescription={product.description}
          defaultImage={product.images[0] || shop?.logoURL || ""}
          shopName={shop?.name}
          productName={product.name}
          availableImages={product.images}
        />
      )}

      {/* Reviews Section */}
      <div className="max-w-3xl">
        <h2 className="text-2xl font-bold mb-8 flex items-center gap-2">
          Reseñas de Clientes 
          <Badge variant="outline" className="ml-2">{reviews.length}</Badge>
        </h2>

        {reviews.length === 0 ? (
          <div className="bg-muted/30 rounded-2xl p-8 text-center">
            <p className="text-muted-foreground">Aún no hay reseñas para este producto.</p>
          </div>
        ) : (
          <div className="space-y-8 max-h-[600px] overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-muted-foreground/20">
            {reviews.map((review) => (
              <div key={review.id} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-bold">{review.authorName}</div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            className={`h-3 w-3 ${star <= review.rating ? "text-yellow-500 fill-yellow-500" : "text-muted"}`} 
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <p className="text-sm leading-relaxed pl-13">
                  {review.comment}
                </p>
                {review.sellerResponse && (
                  <div className="ml-13 bg-muted/50 p-4 rounded-xl border-l-4 border-primary space-y-1">
                    <div className="text-xs font-bold text-primary uppercase tracking-wider">Respuesta del vendedor</div>
                    <p className="text-sm italic">{review.sellerResponse}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
